import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import ClipLoader from 'react-spinners/ClipLoader';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';
import './Analytics.css';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend
);

const Analytics = () => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

    // State
    const [messageStats, setMessageStats] = useState({ total: 0 });
    const [messagesByUser, setMessagesByUser] = useState([]);
    const [callStats, setCallStats] = useState({ totalCalls: 0, totalMinutes: 0, avgDuration: 0 });
    const [callsByUser, setCallsByUser] = useState([]);
    const [callStatus, setCallStatus] = useState({ answered: 0, missed: 0, declined: 0 });
    const [loading, setLoading] = useState(true);

    // User Spotlight state (Phase 4)
    const [allUsers, setAllUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [selectedCallType, setSelectedCallType] = useState('all');
    const [selectedConvType, setSelectedConvType] = useState('all'); // New: Conversation Type

    // Date filter states - Initialize with Last 7 Days
    const getInitialDates = () => {
        const today = new Date();
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        return {
            start: sevenDaysAgo.toISOString().split('T')[0],
            end: today.toISOString().split('T')[0]
        };
    };

    const initialDates = getInitialDates();
    const [startDate, setStartDate] = useState(initialDates.start);
    const [endDate, setEndDate] = useState(initialDates.end);
    const [activeFilter, setActiveFilter] = useState('Last 7 Days');

    // Fetch users list on mount
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const res = await axios.get(`${BACKEND_URL}/api/analytics/users/activity`, {
                    withCredentials: true
                });
                setAllUsers(res.data);
            } catch (error) {
                console.error('Failed to fetch users:', error);
            }
        };
        fetchUsers();
    }, [BACKEND_URL]);

    // Fetch analytics when filters change
    useEffect(() => {
        fetchAnalytics();
    }, [startDate, endDate, selectedUserId, selectedCallType, selectedConvType]);

    const setDatePreset = (preset) => {
        const today = new Date();
        const end = today.toISOString().split('T')[0];
        let start = '';

        switch (preset) {
            case 'today':
                start = end;
                setActiveFilter('Today');
                break;
            case '7days':
                const sevenDaysAgo = new Date(today);
                sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                start = sevenDaysAgo.toISOString().split('T')[0];
                setActiveFilter('Last 7 Days');
                break;
            case '30days':
                const thirtyDaysAgo = new Date(today);
                thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                start = thirtyDaysAgo.toISOString().split('T')[0];
                setActiveFilter('Last 30 Days');
                break;
            case 'month':
                start = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
                setActiveFilter('This Month');
                break;
            case 'all':
                setStartDate('');
                setEndDate('');
                setActiveFilter('All Time');
                return;
            default:
                return;
        }

        setStartDate(start);
        setEndDate(end);
    };

    const handleCustomDateChange = () => {
        if (startDate && endDate) {
            setActiveFilter('Custom Range');
        }
    };

    const clearUserSpotlight = () => {
        setSelectedUserId('');
        setSelectedCallType('all');
        setSelectedConvType('all');
    };

    const fetchAnalytics = async () => {
        setLoading(true);
        try {
            // Build query params
            const params = new URLSearchParams();
            if (startDate && endDate) {
                params.append('startDate', startDate);
                params.append('endDate', endDate);
            }

            // User Spotlight filter
            if (selectedUserId) {
                params.append('userId', selectedUserId);
            }

            // Call Type filter
            if (selectedCallType !== 'all') {
                params.append('callType', selectedCallType);
            }

            // Conversation Type filter (placeholder for future backend support)
            if (selectedConvType !== 'all') {
                params.append('conversationType', selectedConvType);
            }

            const [messagesTotal, messagesByUserData, callsTotal, callsByUserData, callStatusData] = await Promise.all([
                axios.get(`${BACKEND_URL}/api/analytics/messages/total?${params}`, { withCredentials: true }),
                axios.get(`${BACKEND_URL}/api/analytics/messages/by-user?${params}`, { withCredentials: true }),
                axios.get(`${BACKEND_URL}/api/analytics/calls/total?${params}`, { withCredentials: true }),
                axios.get(`${BACKEND_URL}/api/analytics/calls/by-user?${params}`, { withCredentials: true }),
                axios.get(`${BACKEND_URL}/api/analytics/calls/status-breakdown?${params}`, { withCredentials: true })
            ]);

            setMessageStats(messagesTotal.data);
            setMessagesByUser(messagesByUserData.data);
            setCallStats(callsTotal.data);
            setCallsByUser(callsByUserData.data);
            setCallStatus(callStatusData.data);

        } catch (error) {
            console.error('Failed to fetch analytics:', error);
            toast.error('Failed to load analytics data');
        } finally {
            setLoading(false);
        }
    };

    // Chart.js data configurations
    const callStatusChartData = {
        labels: ['Answered', 'Missed', 'Declined'],
        datasets: [{
            data: [callStatus.answered, callStatus.missed, callStatus.declined],
            backgroundColor: ['#43e97b', '#f5576c', '#ffa726'],
            borderWidth: 0,
        }]
    };

    // Calculate total for percentages
    const totalCalls = callStatus.answered + callStatus.missed + callStatus.declined;

    const topCallersChartData = {
        labels: callsByUser.slice(0, 8).map(u => u.name || 'Unknown'),
        datasets: [{
            label: 'Minutes',
            data: callsByUser.slice(0, 8).map(u => parseFloat(u.totalMinutes.toFixed(2))),
            backgroundColor: 'rgba(240, 147, 251, 0.8)',
            borderColor: '#f093fb',
            borderWidth: 2,
            borderRadius: 8,
        }]
    };

    const topSendersChartData = {
        labels: messagesByUser.slice(0, 10).map(u => u.name || 'Unknown'),
        datasets: [{
            label: 'Messages',
            data: messagesByUser.slice(0, 10).map(u => u.totalMessages),
            backgroundColor: 'rgba(102, 126, 234, 0.8)',
            borderColor: '#667eea',
            borderWidth: 2,
            borderRadius: 8,
        }]
    };

    // Custom tooltip to show precise minutes
    const barTooltipCallbacks = {
        label: function (context) {
            return `${context.dataset.label}: ${context.parsed.y.toFixed(2)} min`;
        }
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: true,
                position: 'bottom',
                labels: {
                    font: { size: 12, weight: '600' },
                    padding: 15
                }
            },
            tooltip: {
                backgroundColor: 'rgba(255, 255, 255, 0.98)',
                titleColor: '#00473f',
                bodyColor: '#4a5568',
                borderColor: '#e0e6ed',
                borderWidth: 1,
                padding: 12,
                titleFont: { size: 13, weight: 'bold' },
                bodyFont: { size: 12 },
                boxPadding: 6,
                callbacks: barTooltipCallbacks
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    font: { size: 11 },
                    callback: function (value) {
                        return value.toFixed(2);
                    }
                },
                grid: { color: '#e0e6ed', drawBorder: false }
            },
            x: {
                ticks: {
                    font: { size: 11, weight: '600' },
                    maxRotation: 45,
                    minRotation: 45
                },
                grid: { display: false }
            }
        }
    };

    const pieOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    font: { size: 12, weight: '600' },
                    padding: 15,
                    usePointStyle: true,
                    generateLabels: function (chart) {
                        const data = chart.data;
                        if (data.labels.length && data.datasets.length) {
                            return data.labels.map((label, i) => {
                                const value = data.datasets[0].data[i];
                                const percentage = totalCalls > 0 ? ((value / totalCalls) * 100).toFixed(1) : 0;
                                return {
                                    text: `${label}: ${value} (${percentage}%)`,
                                    fillStyle: data.datasets[0].backgroundColor[i],
                                    hidden: false,
                                    index: i
                                };
                            });
                        }
                        return [];
                    }
                }
            },
            tooltip: {
                backgroundColor: 'rgba(255, 255, 255, 0.98)',
                titleColor: '#00473f',
                bodyColor: '#4a5568',
                borderColor: '#e0e6ed',
                borderWidth: 1,
                padding: 12,
                titleFont: { size: 13, weight: 'bold' },
                bodyFont: { size: 12 },
                callbacks: {
                    label: function (context) {
                        const label = context.label || '';
                        const value = context.parsed;
                        const percentage = totalCalls > 0 ? ((value / totalCalls) * 100).toFixed(1) : 0;
                        return `${label}: ${value} calls (${percentage}%)`;
                    }
                }
            }
        }
    };

    const selectedUser = allUsers.find(u => u._id === selectedUserId);

    const handleRefresh = async () => {
        await fetchAnalytics();
        toast.success('Dashboard updated');
    };

    // Export Data Function
    const exportData = (type) => {
        let data = [];
        let headers = [];
        let filename = `analytics_${type}_${new Date().toISOString().slice(0, 10)}.csv`;

        if (type === 'messages') {
            headers = ['Name', 'Email', 'Total Messages'];
            data = messagesByUser.map(u => [
                u.name || 'Unknown',
                u.email || '-',
                u.totalMessages
            ]);
        } else if (type === 'calls') {
            headers = ['Name', 'Answered', 'Missed', 'Declined', 'Total Minutes', 'Avg Duration (sec)'];
            data = callsByUser.map(u => [
                u.name || 'Unknown',
                u.answered,
                u.missed,
                u.declined,
                u.totalMinutes.toFixed(2),
                Math.round(u.avgDuration)
            ]);
        }

        if (data.length === 0) {
            toast.warn('No data to export');
            return;
        }

        const csvContent = [
            headers.join(','),
            ...data.map(row => row.map(item => `"${item}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            toast.success(`${filename} exported successfully`);
        }
    };

    if (loading && !messageStats.total) { // Initial loading only
        return (
            <div className="analytics-loading">
                <ClipLoader size={50} color="#00473f" />
            </div>
        );
    }

    return (
        <div className="analytics-container px-4">
            <div className="analytics-header">
                <h2>Analytics Dashboard</h2>
                <div className="header-actions">
                    {activeFilter !== 'All Time' && (
                        <span className="active-filter-badge">
                            <i className="fa fa-filter"></i> {activeFilter}
                        </span>
                    )}
                    <button className="refresh-btn" onClick={handleRefresh}>
                        <i className="fa fa-refresh"></i> Refresh
                    </button>
                </div>
            </div>

            {/* Combined Filter Bar */}
            <div className="filters-container mb-4">
                <div className="row g-3">
                    {/* Date Filters Column */}
                    <div className="col-md-5">
                        <div className="filter-card h-100">
                            <label className="filter-label">📅 Date Range</label>
                            <div className="d-flex gap-2 flex-wrap mb-2">
                                <button onClick={() => setDatePreset('today')} className={`preset-btn ${activeFilter === 'Today' ? 'active' : ''}`}>Today</button>
                                <button onClick={() => setDatePreset('7days')} className={`preset-btn ${activeFilter === 'Last 7 Days' ? 'active' : ''}`}>7 Days</button>
                                <button onClick={() => setDatePreset('30days')} className={`preset-btn ${activeFilter === 'Last 30 Days' ? 'active' : ''}`}>30 Days</button>
                                <button onClick={() => setDatePreset('all')} className={`preset-btn ${activeFilter === 'All Time' ? 'active' : ''}`}>All</button>
                            </div>
                            <div className="d-flex gap-2 align-items-center">
                                <input type="date" className="form-control form-control-sm" value={startDate} onChange={(e) => { setStartDate(e.target.value); handleCustomDateChange(); }} />
                                <span className="text-muted">-</span>
                                <input type="date" className="form-control form-control-sm" value={endDate} onChange={(e) => { setEndDate(e.target.value); handleCustomDateChange(); }} />
                            </div>
                        </div>
                    </div>

                    {/* User Spotlight & Advanced Filters Column */}
                    <div className="col-md-7">
                        <div className={`filter-card h-100 ${selectedUserId ? 'spotlight-active-card' : ''}`}>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <label className="filter-label">🔍 User Spotlight & Filters</label>
                                {selectedUserId && (
                                    <button className="btn btn-sm btn-outline-danger py-0" onClick={clearUserSpotlight} style={{ fontSize: '11px' }}>
                                        ✕ Clear
                                    </button>
                                )}
                            </div>

                            <div className="row g-2">
                                <div className="col-md-4">
                                    <select
                                        className="form-select form-select-sm"
                                        value={selectedUserId}
                                        onChange={(e) => setSelectedUserId(e.target.value)}
                                    >
                                        <option value="">All Users (Overview)</option>
                                        {allUsers.map(user => (
                                            <option key={user._id} value={user._id}>{user.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-md-4">
                                    <select
                                        className="form-select form-select-sm"
                                        value={selectedCallType}
                                        onChange={(e) => setSelectedCallType(e.target.value)}
                                    >
                                        <option value="all">📹 Calls: All</option>
                                        <option value="video">Video Only</option>
                                        <option value="audio">Audio Only</option>
                                    </select>
                                </div>
                                <div className="col-md-4">
                                    {/* <select
                                        className="form-select form-select-sm"
                                        value={selectedConvType}
                                        onChange={(e) => setSelectedConvType(e.target.value)}
                                        disabled // Disabled until backend support is added
                                        title="Coming Soon"
                                    >
                                        <option value="all">💬 Chat: All</option>
                                        <option value="direct">Direct</option>
                                        <option value="group">Group</option>
                                    </select> */}
                                </div>
                            </div>

                            {/* Active User Badge */}
                            {selectedUserId && selectedUser && (
                                <div className="mt-2 user-badge d-flex align-items-center gap-2">
                                    <img
                                        src={selectedUser.picture || "https://icon-library.com/images/anonymous-avatar-icon/anonymous-avatar-icon-25.jpg"}
                                        alt={selectedUser.name}
                                        className="rounded-circle"
                                        style={{ width: '24px', height: '24px', objectFit: 'cover' }}
                                    />
                                    <div>
                                        <span className="fw-bold text-dark">{selectedUser.name}</span>
                                        <span className="text-muted ms-2" style={{ fontSize: '11px' }}>{selectedUser.email}</span>
                                    </div>
                                    <span className={`badge ms-auto ${selectedUser.status === 'online' ? 'bg-success' : 'bg-secondary'}`}>
                                        {selectedUser.status || 'Offline'}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Metric Cards */}
            <div className="metrics-grid">
                <div className="metric-card">
                    <div className="metric-icon messages">
                        <i className="fa fa-comments"></i>
                    </div>
                    <div className="metric-content">
                        <h3>{messageStats.total?.toLocaleString()}</h3>
                        <p>{selectedUserId ? 'Messages Sent' : 'Total Messages'}</p>
                    </div>
                </div>

                <div className="metric-card">
                    <div className="metric-icon calls">
                        <i className="fa fa-phone"></i>
                    </div>
                    <div className="metric-content">
                        <h3>{callStats.totalCalls?.toLocaleString()}</h3>
                        <p>{selectedUserId ? 'Total Calls' : 'Total Calls'}</p>
                    </div>
                </div>

                <div className="metric-card">
                    <div className="metric-icon minutes">
                        <i className="fa fa-clock-o"></i>
                    </div>
                    <div className="metric-content">
                        <h3>{Math.round(callStats.totalMinutes || 0).toLocaleString()}</h3>
                        <p>Total Minutes</p>
                    </div>
                </div>

                <div className="metric-card">
                    <div className="metric-icon avg">
                        <i className="fa fa-bar-chart"></i>
                    </div>
                    <div className="metric-content">
                        <h3>{((callStats.avgDuration || 0) / 60).toFixed(2)} min</h3>
                        <p>Avg Call Duration</p>
                    </div>
                </div>
            </div>

            {/* Charts Section with Chart.js */}
            <div className="charts-grid">
                {/* Call Status Pie Chart */}
                <div className="chart-container">
                    <h3>📞 {selectedUserId ? `${selectedUser?.name}'s Call Status` : 'Call Status Breakdown'}</h3>
                    <div style={{ height: '280px' }}>
                        <Pie data={callStatusChartData} options={pieOptions} />
                    </div>
                </div>

                {/* Top Callers Bar Chart */}
                <div className="chart-container">
                    <h3>👥 {selectedUserId ? 'Top Call Partners (Minutes)' : 'Top Callers (Minutes)'}</h3>
                    <div style={{ height: '280px' }}>
                        <Bar data={topCallersChartData} options={chartOptions} />
                    </div>
                </div>
            </div>

            {/* Message Senders - Full Width */}
            <div className="charts-grid-single">
                <div className="chart-container">
                    <h3>💬 {selectedUserId ? 'Top Messaging Partners' : 'Top Message Senders'}</h3>
                    <div style={{ height: '300px' }}>
                        <Bar data={topSendersChartData} options={chartOptions} />
                    </div>
                </div>
            </div>

            {/* Tables */}
            <div className="tables-grid">
                {/* Messages by User Table */}
                <div className="analytics-table-container">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h3>{selectedUserId ? 'Messaging Partners' : 'Messages by User'}</h3>
                        <button className="btn btn-sm btn-light border" onClick={() => exportData('messages')}>
                            <i className="fa fa-download me-1"></i> Export
                        </button>
                    </div>
                    <div className="table-wrapper">
                        <table className="analytics-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>{selectedUserId ? 'Partner Name' : 'Name'}</th>
                                    <th>Email</th>
                                    <th>Total Messages</th>
                                </tr>
                            </thead>
                            <tbody>
                                {messagesByUser.length > 0 ? (
                                    messagesByUser.map((user, index) => (
                                        <tr key={user._id || index}>
                                            <td>{index + 1}</td>
                                            <td>{user.name || 'Unknown'}</td>
                                            <td>{user.email || '-'}</td>
                                            <td><strong>{user.totalMessages}</strong></td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="4" className="no-data">No data available</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Calls by User Table */}
                <div className="analytics-table-container">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h3>{selectedUserId ? 'Call Partners' : 'Calls by User'}</h3>
                        <button className="btn btn-sm btn-light border" onClick={() => exportData('calls')}>
                            <i className="fa fa-download me-1"></i> Export
                        </button>
                    </div>
                    <div className="table-wrapper">
                        <table className="analytics-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>{selectedUserId ? 'Partner Name' : 'Name'}</th>
                                    <th>Status (A/M/D)</th>
                                    <th>Minutes</th>
                                    <th>Avg (sec)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {callsByUser.length > 0 ? (
                                    callsByUser.map((user, index) => (
                                        <tr key={user._id || index}>
                                            <td>{index + 1}</td>
                                            <td>{user.name || 'Unknown'}</td>
                                            <td>
                                                <div style={{
                                                    display: 'flex',
                                                    gap: '4px',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}>
                                                    <span style={{
                                                        background: '#43e97b',
                                                        color: '#fff',
                                                        padding: '2px 6px',
                                                        borderRadius: '4px',
                                                        fontSize: '12px',
                                                        fontWeight: '700',
                                                        minWidth: '24px',
                                                        textAlign: 'center'
                                                    }}>
                                                        {user.answered}
                                                    </span>
                                                    <span style={{
                                                        background: '#f5576c',
                                                        color: '#fff',
                                                        padding: '2px 6px',
                                                        borderRadius: '4px',
                                                        fontSize: '12px',
                                                        fontWeight: '700',
                                                        minWidth: '24px',
                                                        textAlign: 'center'
                                                    }}>
                                                        {user.missed}
                                                    </span>
                                                    <span style={{
                                                        background: '#ffa726',
                                                        color: '#fff',
                                                        padding: '2px 6px',
                                                        borderRadius: '4px',
                                                        fontSize: '12px',
                                                        fontWeight: '700',
                                                        minWidth: '24px',
                                                        textAlign: 'center'
                                                    }}>
                                                        {user.declined}
                                                    </span>
                                                </div>
                                            </td>
                                            <td><strong>{user.totalMinutes.toFixed(2)}</strong></td>
                                            <td>{Math.round(user.avgDuration)}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="no-data">No data available</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Analytics;